import { AppleSignInButton, EmailCodeForm, type EmailCodeSignInStep, GoogleSignInButton, useGoogleRedirectSignIn } from "@serophin/browser";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";
import { AUTH_BUTTON, AUTH_LINK, AUTH_SKIN } from "./authSkin";
import { APPLE_SERVICES_ID, finishSignIn, GOOGLE_WEB_CLIENT_ID, originPath } from "./signInConfig";

export type AuthViewProps = {
  title?: string;
  subtitle?: string;
  /** Where to go once signed in; defaults to the page's `?redirect=` and then `/`. */
  redirect?: string;
};

const TermsNote = () => (
  <div class="text-neutral-600 dark:text-neutral-400 text-center mt-4 text-xs">
    By continuing, you agree to our{" "}
    <a href="/terms" target="_blank" class={AUTH_LINK}>
      Terms of Use
    </a>{" "}
    and{" "}
    <a href="/privacy" target="_blank" class={AUTH_LINK}>
      Privacy Policy
    </a>
    .
  </div>
);

const MagicLinkNote = () => (
  <p class="text-neutral-500 text-center text-sm">The email also holds a sign-in link. Click it instead of typing the code; it expires in 24 hours.</p>
);

type SocialProps = { redirect?: string; busy: boolean; onError: (message: string) => void };

const SocialButtons = ({ redirect, busy, onError }: SocialProps) => (
  <div class="flex flex-col gap-4">
    {GOOGLE_WEB_CLIENT_ID ? (
      <GoogleSignInButton clientId={GOOGLE_WEB_CLIENT_ID} redirectUri={originPath("/auth")} next={redirect} busy={busy} class={AUTH_BUTTON} />
    ) : null}
    {APPLE_SERVICES_ID ? (
      <AppleSignInButton
        servicesId={APPLE_SERVICES_ID}
        redirectUri={originPath("/auth")}
        onSignedIn={(result) => finishSignIn("apple", result, redirect)}
        onError={onError}
        class={AUTH_BUTTON}
      />
    ) : null}
  </div>
);

/**
 * The sign-in body of `/auth` and of the modal for gated actions: Google, Apple and email (a code, with the link in
 * the same email). `/auth` is also the Google redirect URI, so a view mounted there finishes the code exchange.
 */
export const AuthView = ({ title, subtitle, redirect }: AuthViewProps) => {
  const toast = useToast();
  const [step, setStep] = useState<EmailCodeSignInStep>("email");
  const googleBusy = useGoogleRedirectSignIn({
    redirectUri: GOOGLE_WEB_CLIENT_ID ? originPath("/auth") : undefined,
    onSignedIn: (result, next) => finishSignIn("google", result, next ?? redirect),
    onError: toast.error,
  });
  const hasSocial = GOOGLE_WEB_CLIENT_ID !== "" || APPLE_SERVICES_ID !== "";

  return (
    <div>
      <h2 class="text-2xl font-semibold text-neutral-900 dark:text-white text-center mb-2">{title || "Welcome to !tldr"}</h2>
      <p class="text-neutral-600 dark:text-neutral-400 text-center mb-6">{subtitle || "Sign in or create an account"}</p>
      {step === "email" && hasSocial ? (
        <>
          <SocialButtons redirect={redirect} busy={googleBusy} onError={toast.error} />
          <div class="relative my-6 flex justify-center text-sm">
            <span class="px-2 text-neutral-500 dark:text-neutral-400">or</span>
          </div>
        </>
      ) : null}
      <EmailCodeForm onSignedIn={(result) => finishSignIn("email", result, redirect)} next={redirect} skin={AUTH_SKIN} onStepChange={setStep} />
      {step === "code" ? <MagicLinkNote /> : null}
      <TermsNote />
    </div>
  );
};
